import { Fragment, MouseEventHandler, VFC } from 'react'
import {
  ToastContainer as BaseContainer,
  ToastContentProps,
  ToastContainerProps,
  ToastOptions,
} from 'react-toastify'
import type { Theme } from '@opallabs/gem'
import { useTheme, ThemeProvider } from '@opallabs/hooks'
import { Button } from '../Button'
import { Variants } from '../ButtonVariant/variants'
import { Icon } from '../Icon'
import { ParagraphText } from '../Text/ParagraphText'
import { StrongText } from '../Text/StrongText'
import * as styles from './styles'
import { TypeIcon } from './TypeIcon'

import 'react-toastify/dist/ReactToastify.css'

export { toast } from 'react-toastify'

/**
 * <nnarsing@> Eventually it could be a good idea to make use of the
 * `data` property, instead of using component props, but it would
 * upend the way we use toasts today.
 */
type ToastData = unknown

export type ToastComponent<Props = object> = VFC<
  ToastContentProps<ToastData> & Props
>

type ToastProps = ToastContentProps<ToastData> & {
  errorCount?: string
  message?: React.ReactNode
  showProgressWheel?: boolean
  title: string
}

type ActionToastProps = ToastProps & {
  actionText: string
  actionType?: Variants
  onActionClick?: MouseEventHandler<HTMLButtonElement>
}

const useGemTheme = (theme: ToastOptions['theme']): Theme => {
  // Colored theme is not supported
  if (theme === undefined || theme === 'colored') {
    return 'light'
  }

  return theme
}

const ToastBody: React.VFC<ToastProps> = ({
  closeToast,
  errorCount,
  showProgressWheel,
  toastProps: { type, progress },
  title,
  message,
}) => {
  return (
    <Fragment>
      <div css={styles.typeIconContainer({ type })}>
        <TypeIcon
          errorCount={errorCount}
          progress={progress}
          showProgressWheel={showProgressWheel}
          type={type}
        />
      </div>

      <div css={styles.toastBody}>
        <div css={styles.messageContainer}>
          <StrongText css={styles.messageTitle({ type })}>{title}</StrongText>

          <ParagraphText css={styles.message}>{message}</ParagraphText>
        </div>
      </div>

      <div css={styles.closeIconWrapper}>
        <Button
          ariaLabel="Close"
          css={styles.closeIcon}
          hideButtonText
          icon={<Icon glyph="gem-close" />}
          name="close"
          onClick={closeToast}
          variant="ghost"
        />
      </div>
    </Fragment>
  )
}
ToastBody.displayName = 'Lapidary: ToastBody ← Toast'

export const Toast: React.VFC<ToastProps> = (props) => {
  const theme = useGemTheme(props.toastProps.theme)

  return (
    // Explictly set theme provider to accomodate Backbone use cases
    <ThemeProvider theme={theme}>
      <div css={styles.baseToast}>
        <ToastBody {...props} />
      </div>
    </ThemeProvider>
  )
}
Toast.displayName = 'Lapidary: Toast'

export const ActionToast: React.VFC<ActionToastProps> = ({
  actionText,
  actionType,
  onActionClick,
  ...props
}) => {
  const theme = useGemTheme(props.toastProps.theme)

  if (!onActionClick) return <Toast {...props} />

  return (
    // Explictly set theme provider to accomodate Backbone use cases
    <ThemeProvider theme={theme}>
      <div css={styles.baseActionToast}>
        <div css={styles.actionWrapper}>
          <ToastBody {...props} />
        </div>
        <div css={styles.action}>
          <Button
            buttonText={actionText}
            variant={actionType}
            name={actionText}
            onClick={onActionClick}
          />
        </div>
      </div>
    </ThemeProvider>
  )
}
ActionToast.displayName = 'Lapidary: ActionToast'

export const ToastContainer: React.FC<ToastContainerProps> = (props) => {
  const theme = useTheme()

  const defaultContainerProps: ToastContainerProps = {
    toastClassName: 'lapidary-toast',
    bodyClassName: 'lapidary-toast-body',
    closeOnClick: false,
    closeButton: false,
    draggable: false,
    icon: false,
    hideProgressBar: true,
    position: 'bottom-right',
    role: 'alert',
  }

  return (
    <BaseContainer
      {...defaultContainerProps}
      css={styles.reactToastifyOverrides}
      theme={theme}
      {...props}
    />
  )
}
ToastContainer.displayName = 'Lapidary: ToastContainer'
